/**
 * PoweredByGoogle — Google Maps Platform attribution badge.
 *
 * Any container that shows content from Google Places (names, addresses,
 * ratings, reviews, photos, opening hours, etc.) OFF a Google map needs this
 * badge near the top or bottom of THAT container — a list, a sheet, a modal, a
 * dropdown, a card strip. A page-footer badge only covers content that is the
 * whole page (a single list or detail view); a Google-backed strip mid-page
 * among our own content gets its own. One badge per container, never per item.
 * Content on a Google map needs none: the map's own logo counts, as long as
 * nothing of ours covers the map's bottom ~26px (logo left, Terms right).
 *
 * The full rules and a checklist for new surfaces: docs/GOOGLE_ATTRIBUTION.md.
 *
 * Variants:
 *   - 'default' (40px, centered) — page footers, list ends
 *   - 'slim'    (32px, centered) — permanent chrome and panels that can't
 *               spare 40px (the trip planner's bottom bar, sheets, modals)
 *   - 'text'    ("Google Maps" as text, 24px) — where the logo doesn't fit
 *               (autocomplete dropdowns, dense panel headers)
 * `align` ('center' | 'start' | 'end') moves the badge within its row.
 *
 * ## Checked against Google's rules (2026-09-25)
 *
 * Source: developers.google.com/maps/documentation/places/web-service/policies
 * ("Google Maps attribution requirements", page updated 2026-09-24).
 *
 * - The logo is the official Google Maps logo from that page's "Download the
 *   Google Maps attribution assets" zip, unmodified (src/assets/google-maps/).
 *   Gray on light surfaces, White in dark mode — both are the non-outlined
 *   versions, which the policy asks for on plain backgrounds. This badge only
 *   ever sits on a plain surface of its own container, so the outlined
 *   versions (for a logo drawn over a map or photo) aren't shipped.
 * - Logo height must be 16–19dp: h-4 (16px), width auto so the aspect ratio
 *   holds. Don't go below h-4.
 * - Clear space of 10dp left/right/top and 5dp bottom around the logo: px-2.5
 *   plus pt-2.5 (slim) or pt-3 (default). That puts the smallest compliant
 *   badge at 31px, which is why 'slim' is 32px (10 + 16 + 6) rather than
 *   anything tighter.
 * - The text form is "Google Maps" exactly: same capitalisation, never wrapped
 *   (whitespace-nowrap), 12px (the policy allows 12–16sp). It's a brand name,
 *   so both locales carry it untranslated.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import logoGray from '../../assets/google-maps/GoogleMaps_Logo_Gray.svg';
import logoWhite from '../../assets/google-maps/GoogleMaps_Logo_White.svg';

const ALIGN = {
  center: 'justify-center',
  start: 'justify-start',
  end: 'justify-end',
};

const VARIANTS = {
  default: 'px-2.5 pt-3 pb-3',
  slim: 'px-2.5 pt-2.5 pb-1.5',
  text: 'px-2.5 py-1',
};

function PoweredByGoogle({ variant = 'default', align = 'center', className = '' }) {
  const { t } = useTranslation();
  const padding = VARIANTS[variant] || VARIANTS.default;
  const justify = ALIGN[align] || ALIGN.center;

  if (variant === 'text') {
    return (
      <div className={`flex items-center ${justify} ${padding} ${className}`}>
        <span className="text-xs text-content-muted whitespace-nowrap">{t('poweredByGoogle.text')}</span>
      </div>
    );
  }

  return (
    <div className={`flex items-center ${justify} ${padding} ${className}`}>
      <img src={logoGray} alt={t('poweredByGoogle.logoAlt')} className="h-4 w-auto dark:hidden" />
      <img src={logoWhite} alt={t('poweredByGoogle.logoAlt')} className="h-4 w-auto hidden dark:block" />
    </div>
  );
}

export default PoweredByGoogle;
